import * as _ from "underscore";

import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import { TypedEventEmitter } from "shared/TypedEventEmitter";
import * as utils from "shared/utils";

const splitPhraseForLanguage = (phrase: string, language: string) =>
  utils.splitPhraseIntoSpanTokens(
    phrase,
    Boolean(languageMap.map[language]?.noWordBoundaries)
  );

const indexOfPhrase = (splitPhrases, splitPhrase) =>
  _.findIndex(
    splitPhrases,
    (phrase) =>
      // The length check matters: without it a phrase would count as a
      // duplicate of any longer phrase it is a prefix of (e.g. 中国 / 中国人).
      phrase.split.length === splitPhrase.length &&
      _.every(
        phrase.split,
        (word, wordIndex) => word === splitPhrase[wordIndex]
      )
  );

export type AllPhrasesIndexEventPayload = {
  language: string;
  split: string[];
  whole: string;
};

type AllPhrasesIndexEventMap = {
  add: [AllPhrasesIndexEventPayload];
  remove: [AllPhrasesIndexEventPayload];
};

export default class AllPhrasesIndex extends TypedEventEmitter<AllPhrasesIndexEventMap> {
  phraseIndex = {};
  filter: any;

  /**
   * Fetch all the user's words and phrases for a given language from the server.
   *
   * @param {Object} options - Options for fetching phrases.
   * @param {string} options.language - The language to fetch phrases for.
   * @param {"readyToLearn"} [options.filter] - Optional filter to apply to the phrases.
   */
  async fetch(options) {
    const that = this;

    if ((this.filter || options.filter) && options.filter !== this.filter) {
      this.phraseIndex = {};
    }
    this.filter = options.filter;

    // fetch all user's phrases from server (except deleted ones)
    // return all phrases, indexed by the first word
    //
    // TODO: this will make 2 requests if called twice in quick succession
    if (this.phraseIndex[options.language]) {
      return that.phraseIndex[options.language];
    }

    const { data } = await readlangClient.request(
      "GET",
      "/api/listAllWords/" + options.language,
      { filter: this.filter }
    );

    // Check for error, which happens if we've reached the free limit
    if (data.error && data.error.type) {
      throw new Error(data.error.type);
    }

    const allWords = data;
    // Null prototype so first words like "constructor" can't collide with
    // Object.prototype properties when this index is read back.
    const phraseIndex = Object.create(null);
    _.each(allWords, (phrase) => {
      const phraseWords = splitPhraseForLanguage(phrase, options.language);
      const firstWord = phraseWords[0];
      phraseIndex[firstWord] =
        typeof phraseIndex[firstWord] === "object"
          ? phraseIndex[firstWord]
          : [];
      phraseIndex[firstWord].push({
        whole: phrase,
        split: phraseWords,
      });
    });
    that.phraseIndex[options.language] = phraseIndex;
    return phraseIndex;
  }

  addToPhraseIndex(language, phrase) {
    const phraseIndex = this.phraseIndex[language];
    if (!phraseIndex) {
      // this language index hasn't been fetched
      return;
    }

    const splitWords = splitPhraseForLanguage(phrase, language);
    const phrases = phraseIndex[splitWords[0]] || [];

    const index = indexOfPhrase(phrases, splitWords);

    if (index === -1) {
      phrases.push({
        whole: phrase,
        split: splitWords,
      });
      phraseIndex[splitWords[0]] = phrases;
      this.trigger("add", {
        language,
        split: splitWords,
        whole: phrase,
      });
    }
  }

  /**
   * @returns {{
   *   [firstWordLower: string]: { whole: string; split: string[];}
   * }} the new phrase index for this language
   **/
  removeFromPhraseIndex(language, phrase) {
    const phraseIndex = this.phraseIndex[language];
    if (!phraseIndex) {
      // this language index hasn't been fetched
      return;
    }

    const splitWords = splitPhraseForLanguage(phrase, language);

    const phrases = phraseIndex[splitWords[0]] || [];
    const index = indexOfPhrase(phrases, splitWords);
    if (index !== -1) {
      phrases.splice(index, 1);
      phraseIndex[splitWords[0]] = phrases;
      if (indexOfPhrase(phrases, splitWords) === -1) {
        // only trigger remove if it's the last one in case of duplicate phrases
        this.trigger("remove", {
          language,
          split: splitWords,
          whole: phrase,
        });
      }
    }
    return phraseIndex;
  }

  /**
   * @returns {{
   *   [firstWordLower: string]: { whole: string; split: string[];}
   * }} the new phrase index for this language
   **/
  wordRecalled(language, phrase) {
    if (this.filter === "readyToLearn") {
      return this.removeFromPhraseIndex(language, phrase);
    }

    return this.phraseIndex[language];
  }
}
